import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';

export const getVisiblePages = (currentPage, pageCount) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const pages = new Set([0, pageCount - 1]);

  for (let index = currentPage - 1; index <= currentPage + 1; index += 1) {
    if (index >= 0 && index < pageCount) {
      pages.add(index);
    }
  }

  const sortedPages = [...pages].sort((a, b) => a - b);
  const visiblePages = [];

  sortedPages.forEach((page, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage !== undefined && page - previousPage > 1) {
      visiblePages.push('ellipsis');
    }
    visiblePages.push(page);
  });

  return visiblePages;
};

const ITEM_LABEL_KEYS = {
  candidate: { one: 'common.candidate', other: 'common.candidates' },
  alert: { one: 'common.alert', other: 'common.alerts_word' },
  call: { one: 'common.call', other: 'common.calls' },
};

const CandidatesPagination = ({
  currentPage,
  pageCount,
  pageSize,
  totalCount,
  onPageChange,
  itemLabel = 'candidate',
}) => {
  const { t } = useTranslation();
  const visiblePages = useMemo(
    () => getVisiblePages(currentPage, pageCount),
    [currentPage, pageCount],
  );

  if (totalCount <= pageSize) return null;

  const start = currentPage * pageSize + 1;
  const end = Math.min((currentPage + 1) * pageSize, totalCount);
  const labelKeys = ITEM_LABEL_KEYS[itemLabel] || ITEM_LABEL_KEYS.candidate;
  const items = t(totalCount === 1 ? labelKeys.one : labelKeys.other);

  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      <p className="text-xs text-muted-foreground text-center">
        {t('candidates.showingRange', {
          start,
          end,
          total: totalCount,
          items,
        })}
      </p>

      <div className="flex flex-wrap items-center justify-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={currentPage === 0}
          onClick={() => onPageChange(currentPage - 1)}
        >
          {t('common.previous')}
        </Button>

        {visiblePages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-0.5 text-xs text-muted-foreground select-none"
                aria-hidden="true"
              >
                …
              </span>
            );
          }

          return (
            <Button
              key={page}
              type="button"
              variant={page === currentPage ? 'default' : 'outline'}
              size="xs"
              className="h-8 w-8 min-w-8 max-w-8 p-0"
              onClick={() => onPageChange(page)}
              aria-label={t('candidates.goToPage', { page: page + 1 })}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page + 1}
            </Button>
          );
        })}

        <Button
          type="button"
          variant="outline"
          size="xs"
          disabled={currentPage >= pageCount - 1}
          onClick={() => onPageChange(currentPage + 1)}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
};

export default CandidatesPagination;
